import React, { ReactNode, useState } from "react";
import clsx from "clsx";
import { User } from "lucide-react";

// Avatar Component
export interface AvatarProps {
  src?: string;
  alt?: string;
  children?: ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  variant?: "round" | "rounded" | "square";
  status?: "online" | "offline" | "busy" | "away";
  badge?: ReactNode;
  interactive?: boolean;
  disabled?: boolean;
  fallback?: ReactNode;
  className?: string;
  onClick?: () => void;
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = "Avatar",
  children,
  size = "md",
  variant = "round",
  status,
  badge,
  interactive = false,
  disabled = false,
  fallback,
  className,
  onClick,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const sizeStyles = {
    xs: "h-6 w-6 text-xs",
    sm: "h-8 w-8 text-sm",
    md: "h-10 w-10 text-base",
    lg: "h-12 w-12 text-lg",
    xl: "h-16 w-16 text-xl",
  };

  const variantStyles = {
    round: "rounded-full",
    rounded: "rounded-md",
    square: "rounded-none",
  };

  const statusStyles = {
    online: "bg-green-500",
    offline: "bg-gray-500",
    busy: "bg-red-500",
    away: "bg-yellow-500",
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const renderContent = () => {
    if (src && !imageError && imageLoaded) {
      return (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      );
    }

    if (fallback) {
      return fallback;
    }

    if (children) {
      return children;
    }

    return <User className="w-1/2 h-1/2 text-neutral-400" />;
  };

  return (
    <div
      className={clsx(
        "relative inline-flex items-center justify-center overflow-hidden",
        "bg-neutral-700 text-neutral-200",
        sizeStyles[size],
        variantStyles[variant],
        interactive &&
          !disabled &&
          "cursor-pointer hover:ring-2 hover:ring-tracq-bright-blue hover:ring-offset-2 hover:ring-offset-neutral-900 transition-all duration-200",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
      onClick={interactive && !disabled ? onClick : undefined}
    >
      {renderContent()}

      {/* Status Badge */}
      {status && (
        <div
          className={clsx(
            "absolute bottom-0 right-0 w-3 h-3 border-2 border-neutral-900 rounded-full",
            statusStyles[status],
          )}
        />
      )}

      {/* Custom Badge */}
      {badge && <div className="absolute -top-1 -right-1">{badge}</div>}
    </div>
  );
};

// Avatar Group Component
interface AvatarGroupProps {
  children: ReactNode;
  max?: number;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  children,
  max = 3,
  size = "md",
  className,
}) => {
  const childrenArray = React.Children.toArray(children);
  const visibleChildren = childrenArray.slice(0, max);
  const remainingCount = childrenArray.length - max;

  return (
    <div className={clsx("flex -space-x-2", className)}>
      {visibleChildren.map((child, index) => (
        <div key={index} className="relative">
          {child}
        </div>
      ))}
      {remainingCount > 0 && (
        <div
          className={clsx(
            "relative inline-flex items-center justify-center overflow-hidden",
            "bg-neutral-600 text-neutral-200 border-2 border-neutral-900",
            "rounded-full font-medium",
            size === "xs" && "h-6 w-6 text-xs",
            size === "sm" && "h-8 w-8 text-sm",
            size === "md" && "h-10 w-10 text-base",
            size === "lg" && "h-12 w-12 text-lg",
            size === "xl" && "h-16 w-16 text-xl",
          )}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

// Avatar with Text Component
interface AvatarWithTextProps {
  src?: string;
  alt?: string;
  name: string;
  subtitle?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  variant?: "round" | "rounded" | "square";
  status?: "online" | "offline" | "busy" | "away";
  badge?: ReactNode;
  interactive?: boolean;
  disabled?: boolean;
  fallback?: ReactNode;
  className?: string;
  onClick?: () => void;
}

export const AvatarWithText: React.FC<AvatarWithTextProps> = ({
  src,
  alt,
  name,
  subtitle,
  size = "md",
  variant = "round",
  status,
  badge,
  interactive = false,
  disabled = false,
  fallback,
  className,
  onClick,
}) => {
  const textSizeStyles = {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-sm",
    lg: "text-base",
    xl: "text-lg",
  };

  const subtitleSizeStyles = {
    xs: "text-xs",
    sm: "text-xs",
    md: "text-xs",
    lg: "text-sm",
    xl: "text-base",
  };

  return (
    <div
      className={clsx(
        "flex items-center gap-3",
        interactive && !disabled && "cursor-pointer",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
      onClick={interactive && !disabled ? onClick : undefined}
    >
      <Avatar
        {...(src ? { src } : {})}
        {...(alt ? { alt } : {})}
        size={size}
        variant={variant}
        {...(status ? { status } : {})}
        badge={badge}
        interactive={false}
        disabled={disabled}
        fallback={fallback}
      />
      <div className="flex-1 min-w-0">
        <div className={clsx("font-medium text-white truncate", textSizeStyles[size])}>{name}</div>
        {subtitle && (
          <div className={clsx("text-neutral-400 truncate", subtitleSizeStyles[size])}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
};

// Avatar Fallback Component
interface AvatarFallbackProps {
  name: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}

export const AvatarFallback: React.FC<AvatarFallbackProps> = ({ name, className }) => {
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return <div className={clsx("font-semibold", className)}>{getInitials(name)}</div>;
};
